/**
 * 직원 민감 정보 조회 Hook (V92 - httpsCallable 전환)
 *
 * V92 변경사항:
 * - Firestore 직접 접근 → getEmployee callable 사용
 * - V87 Rules 강화로 인한 직접 접근 차단 대응
 *
 * V36 변경사항:
 * - 암호화/마스킹 완전 제거
 * - 항상 원문 직접 표시
 *
 * **권한별 반환 정책:**
 * - system_admin, role_hr, role_exec: 원문 표시 (hasSensitiveAccess: true)
 * - 일반 사용자: 민감 정보 필드 없음
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * 민감 정보 조회 응답 (V36: 원문만 반환)
 */
export interface EmployeeSensitiveInfo {
  /** 내/외국인 구분 */
  nationalityType?: 'domestic' | 'foreign';

  /** 주소 */
  address?: string | null;

  /** 주민등록번호 (원문) */
  residentRegistrationNumber?: string | null;
}

// V92: getEmployee callable 응답 타입
interface GetEmployeeResponse {
  success: boolean;
  employee: {
    nationalityType?: string;
    address?: string;
    residentRegistrationNo?: string;
    [key: string]: any;
  };
  hasSensitiveAccess?: boolean;
}

/**
 * 직원 민감 정보 조회 Hook (V92)
 *
 * V92: getEmployee callable 사용 (Firestore 직접 접근 차단 대응)
 *
 * @param employeeId - 조회할 직원 ID
 * @returns 민감 정보, 로딩 상태, 에러, HR/Admin 권한 여부
 *
 * @example
 * ```tsx
 * const { sensitiveInfo, isLoading, error, hasSensitiveAccess } = useEmployeeSensitiveInfo(employeeId);
 *
 * // HR/Admin만 민감정보 표시
 * {hasSensitiveAccess && <div>{sensitiveInfo?.residentRegistrationNumber}</div>}
 * ```
 */
export function useEmployeeSensitiveInfo(employeeId: string | undefined) {
  // 민감 정보 상태
  const [sensitiveInfo, setSensitiveInfo] = useState<EmployeeSensitiveInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasSensitiveAccess, setHasSensitiveAccess] = useState(false);

  /**
   * V92: getEmployee callable 사용
   */
  useEffect(() => {
    if (!employeeId) {
      setSensitiveInfo(null);
      setHasSensitiveAccess(false);
      return;
    }

    const fetchSensitiveInfo = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (!functions) {
          throw new Error('Firebase Functions가 초기화되지 않았습니다.');
        }

        const getEmployeeFn = httpsCallable<{ employeeId: string }, GetEmployeeResponse>(
          functions,
          'getEmployee'
        );
        const result = await getEmployeeFn({ employeeId });

        if (!result.data.success) {
          throw new Error('직원 정보 조회 실패');
        }

        const employee = result.data.employee;
        setHasSensitiveAccess(result.data.hasSensitiveAccess || false);

        // V92: 민감 정보 매핑 (HR/Admin만 값 존재)
        setSensitiveInfo({
          nationalityType: employee.nationalityType as 'domestic' | 'foreign' | undefined,
          address: employee.address || null,
          residentRegistrationNumber: employee.residentRegistrationNo || null,
        });
      } catch (err: any) {
        console.error('[V92] useEmployeeSensitiveInfo Error:', {
          code: err?.code,
          message: err?.message,
        });
        setError(err as Error);
        setSensitiveInfo(null);
        setHasSensitiveAccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSensitiveInfo();
  }, [employeeId]);

  return {
    sensitiveInfo,
    isLoading,
    error,
    hasSensitiveAccess, // V92: HR/Admin 권한 여부
  };
}
